import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TeamCounts } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_TEAM_COUNTS_QUERY_KEY } from './company-query-keys';

/**
 * The company-wide team counts. Computed by the server so the client doesn't
 * have to hold every team to count them.
 */
export function useFetchTeamCounts(companyId: string): {
    status: 'error' | 'success' | 'pending';
    counts: TeamCounts | undefined;
    refetch: () => void;
} {
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const {
        status,
        data: counts,
        refetch
    } = useQuery({
        queryKey: [FETCH_TEAM_COUNTS_QUERY_KEY, companyId],
        queryFn: () => {
            return openApiRef.current.teams
                .getTeamCounts({ companyId })
                .then((response) => response.data.data.counts);
        },
        // There are no company-wide counts before a company is selected.
        enabled: companyId !== ''
    });

    return {
        status,
        counts,
        refetch: () => {
            void refetch();
        }
    };
}

/**
 * A single team by id.
 *
 * The team settings pages are reachable by deep link, bookmark and refresh, so
 * they can't resolve their team out of a list page that may not contain it.
 * Every paginated list page seeds this cache, so arriving from the list costs
 * no request.
 */
